import React, { useEffect, useRef, useState } from 'react';
import { useAuthSelector } from '../../services/selectors/authSelector';

import SignatureCanvas from 'react-signature-canvas';
import * as Icons from '../../assets/svg/icons';
import { getNormalizedSignatureDataUrl } from '../../utils/helper/signatureCanvas';

export const PatientSignature = ({
	patientSignature,
	setPatientSignature,
	showPatientSignature,
	setShowPatientSignature,
	isRefused,
	setIsRefused,
	signatureErrors,
	setSignatureErrors,
	covid19,
	setCovid19
}) => {

	const canvasRefs = useRef([]);
	const [showSign, setShowSign] = useState(false);
	const { driverSelectedPharmacy, pharmacyInspectionMode } = useAuthSelector();

	const handleClearPatientSignature = (index) => {
		const canvasRef = canvasRefs.current[index];
		if (canvasRef) {
			canvasRef.clear();
			const newSignatures = [...(patientSignature || [null])];
			newSignatures[index] = null;
			setPatientSignature(newSignatures);
		}
	};

	const handleAddPatientSignature = () => {
		if (patientSignature?.length < 5) {
			setPatientSignature(prevSignatures => [...(prevSignatures || []), null]);
		}
	};

	const handlePatientSignatureEnd = (index) => {
		const signatureDataURL = getNormalizedSignatureDataUrl(canvasRefs.current[index]);
		const newSignatures = [...(patientSignature || [null])];
		newSignatures[index] = signatureDataURL;
		setPatientSignature(newSignatures);
		setShowSign(false);
	};

	const handleRemovePatientSignature = (index) => {
		const newSignatures = patientSignature?.filter((_, i) => i !== index) || [];
		setPatientSignature(newSignatures.length ? newSignatures : [null]);

		const canvasRef = canvasRefs.current[index];
		if (canvasRef) {
			canvasRef.clear();
		}
		canvasRefs.current = canvasRefs.current?.filter((_, i) => i !== index);

		canvasRefs.current?.forEach((ref, i) => {
			if (newSignatures[i] && ref) {
				ref.fromDataURL(newSignatures[i]);
			}
		});
	};

	const handleToggleSignatures = () => {
		setShowSign((prevState) => !prevState);
		setShowPatientSignature((prevState) => !prevState);
		setTimeout(() => {
			const signatures = patientSignature || [null];
			signatures.forEach((signature, index) => {
				if (signature) {
					const canvasRef = canvasRefs.current[index];
					if (canvasRef) {
						canvasRef.fromDataURL(signature);
					}
				}
			});
		}, 0);
	};

	useEffect(() => {
		setTimeout(() => {
			const signatures = patientSignature || [null];
			signatures.forEach((signature, index) => {
				if (signature) {
					const canvasRef = canvasRefs.current[index];
					if (canvasRef) {
						canvasRef.fromDataURL(signature);
					}
				}
			});
		}, 0);
	}, [patientSignature]);

	useEffect(() => {
		if (setSignatureErrors && patientSignature?.[0] !== null && patientSignature?.[0] !== undefined) {
			setSignatureErrors(prev => ({ ...prev, patientSign: null }));
		}
	}, [patientSignature]);

	const signatures = Array.isArray(patientSignature) && patientSignature.length ? patientSignature : [null];

	return (
		<div
			className="w-fit p-4 bg-gray-100 rounded-lg mb-3 border border-gray-200"
			style={{ borderColor: signatureErrors?.patientSign ? 'red' : undefined }}
		>
			{/* Header: Patient Signature + caret (^ when expanded, v when collapsed) */}
			<div
				className="flex items-center justify-between cursor-pointer select-none"
				onClick={handleToggleSignatures}
			>
				<h2 className="text-base text-gray-800">Patient Signature</h2>
				{showPatientSignature ? <Icons.PatientSignatureIcon1 /> : <Icons.PatientSignatureIcon2 />}
			</div>

			{showPatientSignature && (
				<>
					{signatures.map((signature, index) => (
						<div key={index} className="mt-3">
							{/* White signature box with light grey border, CLEAR inside bottom-right */}
							<div className="relative w-full max-w-[300px] rounded-lg border border-gray-200 bg-white overflow-hidden">
								<SignatureCanvas
									penColor="black"
									clearOnResize={false}
									canvasProps={{ className: 'signature w-full', style: { height: 'auto', aspectRatio: '3 / 2' } }}
									ref={(ref) => (canvasRefs.current[index] = ref)}
									onEnd={() => handlePatientSignatureEnd(index)}
								/>
								<button
									type="button"
									onClick={() => handleClearPatientSignature(index)}
									className="absolute bottom-2 right-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium uppercase px-3 py-1.5 rounded flex items-center gap-1.5 border border-gray-200"
								>
									CLEAR
								</button>
								{index !== 0 && signatures.length > 1 && (
									<button
										type="button"
										onClick={() => handleRemovePatientSignature(index)}
										className="absolute top-2 right-2 text-xs text-red-800 bg-red-50 rounded-md px-2 py-1 uppercase"
									>
										Remove
									</button>
								)}
							</div>
						</div>
					))}

					{pharmacyInspectionMode === 1 ? null : (
						patientSignature?.length < 5 && driverSelectedPharmacy?.pharmacies?.patient_multi_sign === 1 && (
							<button
								type="button"
								onClick={handleAddPatientSignature}
								className="text-xs mt-3 bg-gray-50 rounded-md px-3 py-1.5 flex items-center gap-2 uppercase border border-gray-200"
							>
								Add Signature
							</button>
						)
					)}

					{/* PATIENT REFUSED TO SIGN. checkbox - below signature area, left */}
					<div className="mt-3 flex items-center gap-2">
						<input
							type="checkbox"
							id="patient-refused-sign"
							checked={!!isRefused}
							onChange={(e) => setIsRefused?.(e.target.checked)}
							className="w-4 h-4 rounded border-gray-300 text-gray-600 focus:ring-gray-400"
						/>
						<label htmlFor="patient-refused-sign" className="text-sm text-gray-700 uppercase">
							PATIENT REFUSED TO SIGN.
						</label>
					</div>
				</>
			)}
		</div>
	);
};

export const UploadTriplicatesPatientSign = ({
	pharmacistSignature,
	setPharmacistSignature,
	signatureErrors,
	setSignatureErrors
}) => {

	const sigRef = useRef();

	const handlePharmacistSignatureEnd = () => {
		const signatureDataURL = getNormalizedSignatureDataUrl(sigRef.current);
		if (signatureDataURL) {
			setPharmacistSignature(signatureDataURL);
		}
	};

	const handleClearSignature = () => {
		sigRef?.current?.clear();
		setPharmacistSignature(null);
	};

	useEffect(() => {
		if (pharmacistSignature && setSignatureErrors) {
			setSignatureErrors(prev => ({ ...prev, pharmacistSign: null }));
		}
	}, [pharmacistSignature]);

	return (
		<div className="p-4 bg-gray-100 rounded-lg mb-3 border border-gray-200" style={{ border: signatureErrors?.pharmacistSign }}>
			<div className="collapse-header flex items-center justify-between">
				<h2 className="text-base font-semibold text-gray-800">Patient Signature</h2>
			</div>
			<div className="mt-3">
				<div className="relative w-full max-w-[300px] rounded-lg border border-gray-200 bg-white overflow-hidden">
					<SignatureCanvas
						penColor="black"
						clearOnResize={false}
						canvasProps={{ className: 'signature w-full', style: { height: 'auto', aspectRatio: '3 / 2' } }}
						ref={sigRef}
						onEnd={handlePharmacistSignatureEnd}
					/>
					<button
						type="button"
						onClick={handleClearSignature}
						className="absolute bottom-2 right-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium uppercase px-3 py-1.5 rounded flex items-center gap-1.5 border border-gray-200"
					>
						<Icons.ClearPatientSignatureIcon />
						CLEAR
					</button>
				</div>
			</div>
		</div>
	);
};
